import { Text, Tooltip } from "@mantine/core";
import { IOtherAttributeConfigApi, IOtherAttributeConfigItemProps } from "@schemas/otherAttributesSchema";
import { useEffect, useState } from "react";
import { FoldableSectionDeprecated, McDropdown, McInput, McTitle, SimpleTagsDeprecated } from "..";
import { formatNumberNb } from "../../app/utils";
import { IAllOtherAttributeFormulaWithGeneratedStrings } from "../../mc-shared/sharedUtils";
import SimpleStampCheck from "../SimpleStampCheck/SimpleStampCheck";

type IConfigWithId = { fieldId: string } & IOtherAttributeConfigItemProps;
interface ISection {
  sectionName: string;
  sortedAttributeConfigs: ([IConfigWithId, IConfigWithId?, IConfigWithId?] | [IConfigWithId])[];
}

type OnSaveType = (
  oldValue: string | number | boolean,
  newValue: string | number | boolean | string[] | number[],
  attributePath: string,
) => void;

function CustomAttributeSection<T>({
  issueLocalId,
  onSave,
  attributeConfig = {},
  data = {} as T,
}: {
  generatedFormulas?: IAllOtherAttributeFormulaWithGeneratedStrings;
  issueLocalId: number;
  attributeConfig: IOtherAttributeConfigApi;
  data: T;
  onSave: OnSaveType;
}) {
  const [sections, setSections] = useState<JSX.Element[]>([]);
  const [unlockedFields, setUnlockedFields] = useState<string[]>([]);

  const toggleLockdeField = (fieldId: string) => {
    if (unlockedFields.includes(fieldId)) {
      setUnlockedFields(unlockedFields.filter((id) => id !== fieldId));
    } else {
      setUnlockedFields([...unlockedFields, fieldId]);
    }
  };

  useEffect(() => {
    const fields = Object.keys(attributeConfig);

    // sort attributes by order
    fields.sort((a, b) => attributeConfig[a].order - attributeConfig[b].order);

    // remove inactive attributes
    const activeFields = fields.filter((attribute) => attributeConfig[attribute].isActive === true);

    // create a sections array with attribute config arrays
    const sectionKeysMap: Map<string, IConfigWithId[]> = new Map();

    activeFields.forEach((fieldId) => {
      const sectionName = attributeConfig[fieldId].section;

      if (sectionKeysMap.has(sectionName) === false) {
        sectionKeysMap.set(sectionName, [] as IConfigWithId[]);
      }
      const sect = sectionKeysMap.get(sectionName);
      const configWithId = { ...attributeConfig[fieldId], fieldId };
      sect.push(configWithId);
    });

    // create a 3 columns array with attribute config arrays for each cell
    const sectionsArray: ISection[] = Array.from(sectionKeysMap).map(([sectionName, attributeConfigs]) => {
      const attributeConfigRows: ([IConfigWithId, IConfigWithId?, IConfigWithId?] | [IConfigWithId])[] = [];
      let rowOfThree: [IConfigWithId, IConfigWithId?, IConfigWithId?] = [null, null, null];
      let columnIndex = 0;

      for (let i = 0; i < attributeConfigs.length; i++) {
        const attributeConfig = attributeConfigs[i];
        if (attributeConfig.typeOfValue === "MULTILINESTRING") {
          if (rowOfThree[0] || rowOfThree[1] || rowOfThree[2]) {
            attributeConfigRows.push(rowOfThree);
            rowOfThree = [null, null, null];
          }

          attributeConfigRows.push([attributeConfig]);
          columnIndex = 0;
          continue;
        }

        rowOfThree[columnIndex] = attributeConfig;
        columnIndex += 1;

        if (columnIndex === 3) {
          attributeConfigRows.push(rowOfThree);
          rowOfThree = [null, null, null];
          columnIndex = 0;
        }

        if (i === attributeConfigs.length - 1 && (rowOfThree[0] || rowOfThree[1] || rowOfThree[2])) {
          attributeConfigRows.push(rowOfThree);
        }
      }

      return { sectionName, sortedAttributeConfigs: attributeConfigRows };
    });

    const _sectionElements = sectionsArray.map((section, index) => (
      <FoldableSectionDeprecated key={section.sectionName}>
        <FoldableSectionDeprecated.Header>
          <div className="section-title">{section.sectionName}</div>
        </FoldableSectionDeprecated.Header>
        <FoldableSectionDeprecated.Body>
          <div className="d-flex flex-column w-100 mt-2 mb-2">
            <div className="d-flex flex-column">
              {section.sortedAttributeConfigs.map((rows, iColumn) => (
                <div className="even-columns mt-2" key={`${iColumn}${index}`}>
                  {rows.map((cell, iField) => {
                    const cellKey = `${index}${iColumn}${iField}`;

                    if (cell == null) {
                      return <div key={cellKey}></div>;
                    }

                    const cellValue = data[cell.fieldId];

                    return (
                      <McTitle title={cell.label} className="mr-4" key={cellKey}>
                        <McTitle.InlineData positionInlineData="before">
                          <Text pt={4} size="xs">
                            {cell?.labelColor && (
                              <i
                                className="fa fa-fw fa-circle"
                                style={{ color: cell?.labelColor, marginLeft: "-0.2rem" }}
                              />
                            )}
                          </Text>
                        </McTitle.InlineData>
                        <McTitle.InlineData positionInlineData="flex-end">
                          <Text size="sm" style={{ whiteSpace: "nowrap" }}>
                            {cell.locked === true && (
                              <i
                                className={`fa fa-fw pointer ${
                                  unlockedFields.includes(cellKey) ? "fa-unlock text-success" : "fa-lock text-muted"
                                }`}
                                onClick={() => toggleLockdeField(cellKey)}
                              />
                            )}
                            {cell.description && (
                              <Tooltip label={cell.description} withArrow zIndex={2042}>
                                <i className="fa fa-info-circle fa-fw text-info" />
                              </Tooltip>
                            )}
                          </Text>
                        </McTitle.InlineData>
                        {cell.typeOfValue === "BOOLEAN" && (
                          <>
                            <div className="d-flex align-items-center h-100">
                              <SimpleStampCheck
                                disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                                toggled={cellValue == null ? null : Boolean(cellValue)}
                                toggle={(newValue) => {
                                  onSave(cellValue, newValue, cell.fieldId);
                                  if (cell.locked === true) {
                                    toggleLockdeField(cellKey);
                                  }
                                }}
                              />
                            </div>
                          </>
                        )}
                        {cell.typeOfValue === "DROPDOWN" && cell.allowMultipleSelectionsInDropdown !== true && (
                          <OtherAttributesDropdown
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            cell={cell}
                            cellValue={cellValue}
                            onSave={(oldValue, newValue, attributePath) => {
                              onSave(oldValue, newValue, attributePath);
                              if (cell.locked === true) {
                                toggleLockdeField(cellKey);
                              }
                            }}
                          />
                        )}
                        {cell.typeOfValue === "DROPDOWN" &&
                          Array.isArray(cell.tableOptions) &&
                          cell.allowMultipleSelectionsInDropdown === true && (
                            <SimpleTagsDeprecated
                              editable={cell.locked !== true || unlockedFields.includes(cellKey) === true}
                              activeTags={convertValueToArray(cellValue)}
                              tagOptions={cell.tableOptions
                                ?.map((option) => String(option.value))
                                .filter((value) => convertValueToArray(cellValue).includes(value) === false)}
                              onChange={(value) => {
                                const currentValue = convertValueToArray(cellValue);
                                const hasChanged =
                                  JSON.stringify([...currentValue].sort()) !== JSON.stringify([...value].sort());

                                if (hasChanged) {
                                  onSave(cellValue, mapDropdownStringsToValues(value, cell.dropdownOptionType), cell.fieldId);
                                }
                              }}
                            />
                          )}
                        {cell.typeOfValue === "STRING" && (
                          <McInput
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            type="text"
                            onBlur={(e) => {
                              onSave(cellValue, e.target.value, cell.fieldId);
                              if (cell.locked === true) {
                                toggleLockdeField(cellKey);
                              }
                            }}
                            value={cellValue}
                          />
                        )}
                        {cell.typeOfValue === "MULTILINESTRING" && (
                          <McInput
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            type={"textarea"}
                            dynamicHeight={true}
                            onBlur={(e) => {
                              onSave(cellValue, e.target.value, cell.fieldId);
                              if (cell.locked === true) {
                                toggleLockdeField(cellKey);
                              }
                            }}
                            value={cellValue}
                          />
                        )}
                        {cell.typeOfValue === "NUMBER" && (
                          <McInput
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            type={"number"}
                            onBlur={(e) => {
                              onSave(cellValue, e.target.value === "" ? null : Number(e.target.value), cell.fieldId);
                              if (cell.locked === true) {
                                toggleLockdeField(cellKey);
                              }
                            }}
                            value={cellValue}
                          />
                        )}
                        {cell.typeOfValue === "DATE" && (
                          <McInput
                            disabled={cell.locked === true && unlockedFields.includes(cellKey) === false}
                            type="date"
                            onBlur={(e) => {
                              onSave(cellValue, e.target.value, cell.fieldId);
                              if (cell.locked === true) {
                                toggleLockdeField(cellKey);
                              }
                            }}
                            value={cellValue}
                          />
                        )}
                        {(cell.typeOfValue === "FORMULA" || cell.typeOfValue === "MATH_FORMULA") && <>{cellValue}</>}
                      </McTitle>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </FoldableSectionDeprecated.Body>
      </FoldableSectionDeprecated>
    ));

    setSections(_sectionElements);
  }, [data, attributeConfig, unlockedFields]);

  return <>{sections}</>;
}

export default CustomAttributeSection;

const convertValueToArray = (value: any): string[] => {
  try {
    if (Array.isArray(value) && value.every((v) => typeof v === "string" || typeof v === "number")) {
      return value.map((v) => String(v));
    }
    return [];
  } catch (error) {
    return [];
  }
};

const mapDropdownStringsToValues = (
  value: string[],
  dropdownOptionType: IOtherAttributeConfigItemProps["dropdownOptionType"],
) => {
  if (dropdownOptionType !== "NUMBER") {
    return value;
  }

  return value
    .map((item) => Number(item))
    .filter((item) => Number.isNaN(item) === false && Number.isFinite(item));
};

const OtherAttributesDropdown: React.FC<{
  cell: IConfigWithId;
  cellValue: any;
  onSave: OnSaveType;
  disabled?: boolean;
}> = ({ cell, cellValue, onSave, disabled = false }) => {
  const isNumberDropdown = cell.dropdownOptionType === "NUMBER";
  const _options = cell?.tableOptions?.map((option) => {
    const formattedValue = isNumberDropdown ? formatNumberNb(option.value) : String(option.value);

    return {
      value: String(option.value),
      label: formattedValue,
      rawValue: option.value,
      color: option?.color,
      valueAndDescription: `${formattedValue}${
        option.description != null && option.description !== "" ? " - " + option.description : ""
      }`,
    };
  });
  const hasColor = _options?.some((opt) => opt?.color && opt.color?.length > 0);

  return (
    <McDropdown
      disabled={disabled}
      currentValue={_options?.find((option) => String(option.rawValue) === String(cellValue))}
      onClear={cellValue != null ? () => onSave(cellValue, null, cell.fieldId) : undefined}
      options={_options}
      displayKey="valueAndDescription"
      selectedDisplayKey={isNumberDropdown ? "label" : "value"}
      onChange={(_, newValue) => onSave(cellValue, newValue.rawValue, cell.fieldId)}
      type={hasColor ? "colordot" : "text"}
    />
  );
};
